/**
 * TradeUtils — shared utility functions for the trade portal.
 *
 * Provides formatting, sanitization, DOM helpers, page/search controllers,
 * and UI binding functions used across quotes, orders, and detail views.
 * Loaded globally — all functions are attached to window.
 *
 * Usage:
 *   const amount = window.formatTradeAmount(1234.5);
 *   const html = window.renderTradeState({ type: 'error', containerEl: el });
 */

/* ── constants ── */

const US_STATES = [
	'Alabama',
	'Alaska',
	'American Samoa',
	'Arizona',
	'Arkansas',
	'California',
	'Colorado',
	'Connecticut',
	'Delaware',
	'District of Columbia',
	'Florida',
	'Georgia',
	'Guam',
	'Hawaii',
	'Idaho',
	'Illinois',
	'Indiana',
	'Iowa',
	'Kansas',
	'Kentucky',
	'Louisiana',
	'Maine',
	'Maryland',
	'Massachusetts',
	'Michigan',
	'Minnesota',
	'Mississippi',
	'Missouri',
	'Montana',
	'Nebraska',
	'Nevada',
	'New Hampshire',
	'New Jersey',
	'New Mexico',
	'New York',
	'North Carolina',
	'North Dakota',
	'Northern Mariana Islands',
	'Ohio',
	'Oklahoma',
	'Oregon',
	'Pennsylvania',
	'Puerto Rico',
	'Rhode Island',
	'South Carolina',
	'South Dakota',
	'Tennessee',
	'Texas',
	'U.S. Virgin Islands',
	'Utah',
	'Vermont',
	'Virginia',
	'Washington',
	'West Virginia',
	'Wisconsin',
	'Wyoming',
];

const SPECIAL_SHIPPING_ZIP_CODES = [
	'02535',
	'02539',
	'02552',
	'02554',
	'02557',
	'02561',
	'02564',
	'02568',
	'02575',
	'02807',
	'04108',
	'04541',
	'04848',
	'04853',
	'04863',
	'11770',
	'11782',
	'11964',
	'23440',
	'33040',
	'43456',
	'49757',
	'70091',
	'90704',
	'98236',
	'98239',
	'98249',
	'98250',
	'98253',
	'98260',
	'98261',
	'98279',
	'98280',
	'99587',
	'99615',
	'99801',
	'99824',
	'99901',
	'99921',
	'99950',
];

/* ── formatting & sanitization ── */

/**
 * Renders US state <option> elements, marking one as selected.
 * @param {string} selectedState - State name to mark as selected
 * @returns {string} HTML string of <option> elements
 */
function renderStateOptions(selectedState) {
	return US_STATES.map(function (state) {
		return (
			'<option value="' +
			state +
			'"' +
			(selectedState === state ? ' selected' : '') +
			'>' +
			state +
			'</option>'
		);
	}).join('\n');
}

/**
 * Checks whether a ZIP code requires custom (non-standard) shipping.
 * @param {string} zipCode - Full or partial US ZIP code
 * @returns {boolean} True if the ZIP requires custom shipping
 */
function requiresCustomShipping(zipCode) {
	if (!zipCode) return false;

	const zipMatch = zipCode.trim().match(/^(\d{5})/);
	const zip = zipMatch ? zipMatch[1] : '';
	if (!zip) return false;

	// Alaska (995-999) and Hawaii (967-968) always require custom shipping.
	if (
		zip.startsWith('995') ||
		zip.startsWith('996') ||
		zip.startsWith('997') ||
		zip.startsWith('998') ||
		zip.startsWith('999') ||
		zip.startsWith('967') ||
		zip.startsWith('968')
	) {
		return true;
	}

	return SPECIAL_SHIPPING_ZIP_CODES.includes(zip);
}

/**
 * Formats a numeric amount as a US-dollar currency string.
 * @param {number|string} amount - Value to format
 * @returns {string} Formatted currency string (e.g. "$1,234.50")
 */
function formatTradeAmount(amount) {
	let value = typeof amount === 'number' ? amount : Number(amount);
	if (isNaN(value)) value = 0;
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
	}).format(value);
}

/**
 * Escapes a value for safe HTML insertion.
 * @param {*} value - Value to escape
 * @returns {string} HTML-safe string
 */
function escapeTradeHtml(value) {
	if (value === null || value === undefined) return '';
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

/* ── sorting ── */

/**
 * Sorts an array of items by their creation date.
 * @param {Array} items - Items to sort
 * @param {'recent'|'oldest'} sortOrder - Sort direction
 * @param {Function} [getDateValue] - Optional accessor for the date field
 * @returns {Array} New sorted array
 */
function sortByCreatedAt(items, sortOrder, getDateValue) {
	const list = Array.isArray(items) ? items.slice() : [];
	const dateAccessor =
		typeof getDateValue === 'function'
			? getDateValue
			: function (item) {
					return item && item.createdAt;
				};

	const getTime = function (item) {
		const dateStr = dateAccessor(item);
		const time = dateStr ? new Date(dateStr).getTime() : 0;
		return isNaN(time) ? 0 : time;
	};

	if (sortOrder === 'oldest') {
		return list.sort(function (a, b) {
			return getTime(a) - getTime(b);
		});
	}

	return list.sort(function (a, b) {
		return getTime(b) - getTime(a);
	});
}

/* ── UI state ── */

/**
 * Renders an error or empty-state message as an HTML string.
 * @param {Object} config - State configuration (type, title, message, actionText, actionUrl, onRetry, containerEl)
 * @returns {string} State HTML string
 */
function renderTradeState(config) {
	const settings = config || {};
	const type = settings.type || 'error';
	const title = settings.title;
	const message = settings.message;
	const actionText = settings.actionText;
	const actionUrl = settings.actionUrl;
	const onRetry = settings.onRetry;
	const containerEl = settings.containerEl || null;
	const contactEmail = settings.contactEmail || 'trade@harbouroutdoor.com';

	const defaults = {
		error: {
			title: 'Temporarily Unavailable',
			message:
				'We were unable to load this information at the moment. Please try again shortly, or contact our trade support team for assistance.',
		},
		empty: {
			title: 'Nothing here yet',
			message: '',
		},
	};

	const defaultState = defaults[type] || defaults.error;
	const stateTitle = title || defaultState.title;
	const stateMessage = message || defaultState.message;

	const iconHTML =
		type === 'error'
			? '<div class="trade-state__icon"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div>'
			: '';

	const messageHTML = stateMessage
		? '<p class="trade-state__message text-md">' + stateMessage + '</p>'
		: '';

	let actionHTML = '';
	if (actionText) {
		if (actionUrl) {
			actionHTML =
				'<div class="trade-state__actions"><a href="' +
				actionUrl +
				'" class="Button Button--primary Button--small">' +
				actionText +
				'</a></div>';
		} else {
			actionHTML =
				'<div class="trade-state__actions"><button class="Button Button--primary Button--small trade-state__retry-btn">' +
				actionText +
				'</button></div>';
		}
	}

	const contactHTML =
		type === 'error'
			? '<div class="trade-state__contact"><a href="mailto:' +
				contactEmail +
				'" class="text-sm text--muted">' +
				contactEmail +
				'</a></div>'
			: '';

	const html =
		'<div class="trade-state trade-state--' +
		type +
		'">' +
		iconHTML +
		'<h2 class="trade-state__title text-lg">' +
		stateTitle +
		'</h2>' +
		messageHTML +
		actionHTML +
		contactHTML +
		'</div>';

	if (onRetry) {
		setTimeout(function () {
			const retryBtn = (containerEl || document).querySelector('.trade-state__retry-btn');
			if (retryBtn) retryBtn.addEventListener('click', onRetry);
		}, 0);
	}

	return html;
}

/* ── page controllers ── */

/**
 * Creates a controller that manages list/detail view toggling based on URL params.
 * @param {Object} config - Controller options (idParam, listViewEl, detailViewEl, loadingStateEl, etc.)
 * @returns {Object} Controller with getIdFromURL, toggleView, showLoading, initialize methods
 */
function createTradePageController(config) {
	const settings = config || {};
	const idParam = settings.idParam || 'id';
	const listViewEl = settings.listViewEl || null;
	const detailViewEl = settings.detailViewEl || null;
	const loadingStateEl = settings.loadingStateEl || null;
	const loadingMessageEl = settings.loadingMessageEl || null;
	const defaultLoadingMessage = settings.defaultLoadingMessage || 'Loading...';
	const hiddenClass = 'trade-modal__section--hidden';

	function hideElement(element) {
		if (!element || !element.classList) return;
		element.classList.add(hiddenClass);
	}

	function showElement(element) {
		if (!element || !element.classList) return;
		element.classList.remove(hiddenClass);
	}

	function getIdFromURL() {
		try {
			const urlParams = new URLSearchParams(window.location.search);
			return urlParams.get(idParam);
		} catch (error) {
			console.error('Error getting URL params:', error);
			return null;
		}
	}

	function toggleView() {
		const id = getIdFromURL();
		if (id) {
			hideElement(listViewEl);
			showElement(detailViewEl);
		} else {
			showElement(listViewEl);
			hideElement(detailViewEl);
		}
		hideElement(loadingStateEl);
	}

	function showLoading(message) {
		const loadingText = message || defaultLoadingMessage;
		if (loadingMessageEl) loadingMessageEl.textContent = loadingText;
		hideElement(listViewEl);
		hideElement(detailViewEl);
		showElement(loadingStateEl);
	}

	function initialize(loadOne, loadAll) {
		const id = getIdFromURL();
		if (id) {
			loadOne(id);
		} else {
			loadAll();
		}
	}

	return {
		getIdFromURL: getIdFromURL,
		toggleView: toggleView,
		showLoading: showLoading,
		initialize: initialize,
	};
}

/**
 * Creates a search + sort controller with debounced input and immediate sort.
 * @param {Object} config - Options (searchInput, sortSelect, filterFn, sortFn, renderFn, debounceMs, initialItems)
 * @returns {Object} Controller with apply, setItems, bind methods
 */
function createTradeSearchSortController(config) {
	const settings = config || {};
	const searchInput = settings.searchInput || null;
	const sortSelect = settings.sortSelect || null;
	const filterFn =
		settings.filterFn ||
		function (items) {
			return items;
		};
	const sortFn =
		settings.sortFn ||
		function (items) {
			return items;
		};
	const renderFn = settings.renderFn || function () {};
	const debounceMs = typeof settings.debounceMs === 'number' ? settings.debounceMs : 1000;
	let sourceItems = Array.isArray(settings.initialItems) ? settings.initialItems : [];
	let searchTimeout = null;

	function apply() {
		const searchTerm = searchInput ? searchInput.value : '';
		const sortOrder = sortSelect ? sortSelect.value : 'recent';
		const filtered = filterFn(sourceItems, searchTerm);
		const sorted = sortFn(filtered, sortOrder);
		renderFn(sorted);
		return sorted;
	}

	function setItems(items) {
		sourceItems = Array.isArray(items) ? items : [];
	}

	function bind() {
		if (searchInput) {
			searchInput.addEventListener('input', function () {
				clearTimeout(searchTimeout);
				searchTimeout = setTimeout(function () {
					apply();
				}, debounceMs);
			});
		}

		if (sortSelect) {
			sortSelect.addEventListener('change', function () {
				apply();
			});
		}
	}

	return {
		apply: apply,
		setItems: setItems,
		bind: bind,
	};
}

/* ── fetch flow ── */

/**
 * Runs an async fetch with before/after lifecycle hooks and error handling.
 * @param {Object} config - Flow options (before, request, onSuccess, onError, after)
 * @returns {Promise<*>} Result from request(), or null on error
 */
async function runTradeFetchFlow(config) {
	const settings = config || {};
	const before = settings.before;
	const request = settings.request;
	const onSuccess = settings.onSuccess;
	const onError = settings.onError;
	const after = settings.after;

	try {
		if (typeof before === 'function') before();
		const result = await request();
		if (typeof onSuccess === 'function') onSuccess(result);
		return result;
	} catch (error) {
		if (typeof onError === 'function') onError(error);
		return null;
	} finally {
		if (typeof after === 'function') after();
	}
}

/* ── UI bindings ── */

/**
 * Binds click-to-toggle behavior on the mobile summary panel.
 * @param {Object} config - Options (root, summarySelector, toggleSelector, mobileMax)
 * @returns {Function} Unbind function to remove the listener
 */
function bindTradeSummaryToggle(config) {
	const settings = config || {};
	const root = settings.root || document;
	const summarySelector = settings.summarySelector || '.quote-summary';
	const toggleSelector = settings.toggleSelector || '#quote-summary-toggle';
	const mobileMax = typeof settings.mobileMax === 'number' ? settings.mobileMax : 768;

	function clickHandler(event) {
		const target = event.target;
		if (!target) return;
		if (!(target.id === toggleSelector.replace('#', '') || target.closest(toggleSelector)))
			return;
		event.preventDefault();
		const summary = document.querySelector(summarySelector);
		if (summary && window.innerWidth <= mobileMax) {
			summary.classList.toggle('collapsed');
		}
	}

	root.addEventListener('click', clickHandler);
	return function unbind() {
		root.removeEventListener('click', clickHandler);
	};
}

/**
 * Resets the summary collapse state when resizing past the mobile breakpoint.
 * @param {Object} config - Options (summarySelector, desktopMin)
 * @returns {Function} Unbind function to remove the listener
 */
function bindTradeSummaryResizeReset(config) {
	const settings = config || {};
	const summarySelector = settings.summarySelector || '.quote-summary';
	const desktopMin = typeof settings.desktopMin === 'number' ? settings.desktopMin : 769;

	function resizeHandler() {
		const summary = document.querySelector(summarySelector);
		if (summary && window.innerWidth >= desktopMin) {
			summary.classList.remove('collapsed');
		}
	}

	window.addEventListener('resize', resizeHandler);
	return function unbind() {
		window.removeEventListener('resize', resizeHandler);
	};
}

/**
 * Binds click navigation for list items and product images (responsive).
 * @param {Object} config - Options (root, mobileMax, onMobileListClick, onDesktopListClick, etc.)
 * @returns {Function} Unbind function to remove the listener
 */
function bindTradeItemNavigation(config) {
	const settings = config || {};
	const root = settings.root || document;
	const mobileMax = typeof settings.mobileMax === 'number' ? settings.mobileMax : 768;
	const onMobileListClick = settings.onMobileListClick || function () {};
	const onDesktopListClick = settings.onDesktopListClick || onMobileListClick;
	const shouldSkipMobileProductClick =
		settings.shouldSkipMobileProductClick ||
		function () {
			return false;
		};
	const shouldSkipDesktopImageClick =
		settings.shouldSkipDesktopImageClick ||
		function () {
			return false;
		};

	function clickHandler(event) {
		const target = event.target;
		if (!target) return;
		const clickableListItem = target.closest('.trade-data-list__row--clickable');

		if (clickableListItem && !target.closest('a') && !target.closest('button')) {
			if (window.innerWidth <= mobileMax) {
				onMobileListClick(clickableListItem);
			} else {
				onDesktopListClick(clickableListItem);
			}
			return;
		}

		if (window.innerWidth <= mobileMax) {
			const listItem = target.closest('.trade-list-item');
			if (listItem && !target.closest('a') && !target.closest('button')) {
				onMobileListClick(listItem);
				return;
			}

			const mobileProduct = target.closest('.quote-item');
			if (
				mobileProduct &&
				!target.closest('a') &&
				!target.closest('button') &&
				!target.closest('.qty-selector')
			) {
				if (shouldSkipMobileProductClick(mobileProduct, target)) return;
				const mobileLink = mobileProduct.querySelector('.open-product-link');
				if (mobileLink && mobileLink.href) {
					window.location.href = mobileLink.href;
				}
			}
			return;
		}

		const imageEl = target.closest('.quote-item-image');
		if (!imageEl) return;
		const parentItem = imageEl.closest('.quote-item');
		if (shouldSkipDesktopImageClick(parentItem, target)) return;
		const link = parentItem ? parentItem.querySelector('.open-product-link') : null;
		if (link && link.href) {
			window.location.href = link.href;
		}
	}

	root.addEventListener('click', clickHandler);
	return function unbind() {
		root.removeEventListener('click', clickHandler);
	};
}

/**
 * Handles click on an export-PDF button, opening the export modal.
 * @param {Object} config - Options (event, buttonId, getRecordId, endpointBuilder, label, dateLabel)
 * @returns {boolean} False if click was not on the target button, true otherwise
 */
function handleTradeExportPdfClick(config) {
	const settings = config || {};
	const event = settings.event;
	const target = event && event.target;
	const buttonId = settings.buttonId;
	if (!target || !buttonId || target.id !== buttonId) return false;

	event.preventDefault();
	const id = settings.getRecordId && settings.getRecordId();
	if (!id) return true;

	const button = target.closest('#' + buttonId) || target;
	const modal = window.exportPdfModal;
	if (!modal || typeof modal.open !== 'function') return true;

	modal.open(settings.endpointBuilder(id), {
		label: settings.label,
		id: button.dataset.exportDisplayId || '',
		title: button.dataset.exportTitle,
		date: button.dataset.exportDate,
		dateLabel: settings.dateLabel,
		total: button.dataset.exportTotal,
	});

	return true;
}

/* ── DOM helpers ── */

/**
 * Clones the content of a <template> element by its ID.
 * @param {string} templateId - The template element's ID attribute
 * @returns {DocumentFragment} Cloned template content
 */
function cloneTemplate(templateId) {
	const tpl = document.getElementById(templateId);
	if (!tpl) {
		console.error('Template not found: ' + templateId);
		return document.createDocumentFragment();
	}
	return tpl.content.cloneNode(true);
}

/**
 * Validates and sanitizes a URL, allowing only relative paths and https.
 * @param {string} url - URL to sanitize
 * @returns {string|null} Safe URL string, or null if invalid
 */
function sanitizeTradeUrl(url) {
	if (!url) return null;
	const s = String(url);
	if (s.startsWith('//')) return null;
	if (s.startsWith('/') || s.startsWith('https://')) return s;
	return null;
}

/**
 * Assembles header, content, and footer fragments into a container element.
 * @param {Object} config - Scaffold options (containerEl, buildHeader, buildContent, buildFooter, hasContent, includeFooter)
 */
function renderTradeDetailScaffold(config) {
	const settings = config || {};
	const hasContent =
		typeof settings.hasContent === 'function'
			? settings.hasContent
			: function () {
					return true;
				};
	const includeFooter =
		typeof settings.includeFooter === 'function'
			? settings.includeFooter
			: function () {
					return true;
				};
	const fragment = document.createDocumentFragment();
	fragment.appendChild(settings.buildHeader());
	fragment.appendChild(settings.buildContent());
	if (hasContent() && includeFooter()) {
		fragment.appendChild(settings.buildFooter());
	}
	if (settings.containerEl) {
		settings.containerEl.replaceChildren(fragment);
	}
}

const TRADE_ARROW_ICON_SVG =
	'<svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><rect width="14" height="15.75" transform="matrix(0 -1 -1 0 15.75 14.5)" fill="url(#pattern0_118_10454)"/><defs><pattern id="pattern0_118_10454" patternContentUnits="objectBoundingBox" width="1" height="1"><use xlink:href="#image0_118_10454" transform="matrix(0.00878906 0 0 0.0078125 -0.0625 0)"/></pattern><image id="image0_118_10454" width="128" height="128" preserveAspectRatio="none" xlink:href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIAAAACACAMAAAD04JH5AAAAA3NCSVQICAjb4U/gAAAACXBIWXMAAARiAAAEYgEL1D0fAAAAGXRFWHRTb2Z0d2FyZQB3d3cuaW5rc2NhcGUub3Jnm+48GgAAAG9QTFRF////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA62mwiwAAACR0Uk5TAAEEEBESGhw1Njc4OUhLb3BxcnN2h6+wsbK6xN/g4ers+fr+NRR0CwAAAbJJREFUeNrtlcm2gjAQBaPiPKE4zw/y/9/4FERlaAwh3W5urQwuLqk6R5UCAAAAAAAAAAAAAODBaiK/OVm9P8+iMGjJzreCMJqlh/5Na73vSu539/fJWz85tI/6wWUstz++xJPHdnxa64QwkNoPwufk+nGaRjpFJkOsPyGaKtW76jcSGZ76E649ddCf8Gd46U84qOEp84A5w4f+mNNAqc4m+4wzQ0b/nU0nfjwPhTLk9Id++kUhg8cx75XoT5HIQOhP8XN2lq73l5R+KsPOaQZvR+unMpxH7vZH50r9KfM/pgxf9fNmMNHPmcFQP1sGY/08Gero58hQUz/1o7Sw3V/U1e82g41+lxks9TvLYK2fyrCtlcHb2ut3kaGh/sYZGutvlsGFfjLD0OCt3ei3zuBMv10Gl/qpDKeh+ds21E/9RfumvXzliEKG0ot1GPTXycCk3zgDm36zCxYUDZRzqjIw6/+agV1/9UUl9FdlENJPZhDTT2WQ009lENRPZBDUX51BQn9VBiH9VAY5/eUZJPWXZRDWn88grz+b4Rf6PzP8SP8rg68AAAAAAAAAAAAAAGjGP2viy8cJC4zmAAAAAElFTkSuQmCC"/></defs></svg>';
const TRADE_OPEN_PRODUCT_ICON_SVG =
	'<svg width="11" height="10" viewBox="0 0 11 10" fill="none" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><rect y="10" width="10" height="11" transform="rotate(-90 0 10)" fill="url(#pattern0_396_1931)"/><defs><pattern id="pattern0_396_1931" patternContentUnits="objectBoundingBox" width="1" height="1"><use xlink:href="#image0_396_1931" transform="matrix(0.00859375 0 0 0.0078125 -0.05 0)"/></pattern><image id="image0_396_1931" width="128" height="128" preserveAspectRatio="none" xlink:href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIAAAACACAMAAAD04JH5AAAAA3NCSVQICAjb4U/gAAAACXBIWXMAAARiAAAEYgEL1D0fAAAAGXRFWHRTb2Z0d2FyZQB3d3cuaW5rc2NhcGUub3Jnm+48GgAAAG9QTFRF////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA62mwiwAAACR0Uk5TAAEEEBESGhw1Njc4OUhLb3BxcnN2h6+wsbK6xN/g4ers+fr+NRR0CwAAAbJJREFUeNrtlcm2gjAQBaPiPKE4zw/y/9/4FERlaAwh3W5urQwuLqk6R5UCAAAAAAAAAAAAAODBaiK/OVm9P8+iMGjJzreCMJqlh/5Na73vSu539/fJWz85tI/6wWUstz++xJPHdnxa64QwkNoPwufk+nGaRjpFJkOsPyGaKtW76jcSGZ76E649ddCf8Gd46U84qOEp84A5w4f+mNNAqc4m+4wzQ0b/nU0nfjwPhTLk9Id++kUhg8cx75XoT5HIQOhP8XN2lq73l5R+KsPOaQZvR+unMpxH7vZH50r9KfM/pgxf9fNmMNHPmcFQP1sGY/08Gero58hQUz/1o7Sw3V/U1e82g41+lxks9TvLYK2fyrCtlcHb2ut3kaGh/sYZGutvlsGFfjLD0OCt3ei3zuBMv10Gl/qpDKeh+ds21E/9RfumvXzliEKG0ot1GPTXycCk3zgDm36zCxYUDZRzqjIw6/+agV1/9UUl9FdlENJPZhDTT2WQ009lENRPZBDUX51BQn9VBiH9VAY5/eUZJPWXZRDWn88grz+b4Rf6PzP8SP8rg68AAAAAAAAAAAAAAGjGP2viy8cJC4zmAAAAAElFTkSuQmCC"/></defs></svg>';

/* ── exports ── */

window.US_STATES = US_STATES;
window.SPECIAL_SHIPPING_ZIP_CODES = SPECIAL_SHIPPING_ZIP_CODES;
window.renderStateOptions = renderStateOptions;
window.requiresCustomShipping = requiresCustomShipping;
window.formatTradeAmount = formatTradeAmount;
window.escapeTradeHtml = escapeTradeHtml;
window.sortByCreatedAt = sortByCreatedAt;
window.renderTradeState = renderTradeState;
window.createTradePageController = createTradePageController;
window.createTradeSearchSortController = createTradeSearchSortController;
window.runTradeFetchFlow = runTradeFetchFlow;
window.bindTradeSummaryToggle = bindTradeSummaryToggle;
window.bindTradeSummaryResizeReset = bindTradeSummaryResizeReset;
window.bindTradeItemNavigation = bindTradeItemNavigation;
window.handleTradeExportPdfClick = handleTradeExportPdfClick;
window.renderTradeDetailScaffold = renderTradeDetailScaffold;
window.cloneTemplate = cloneTemplate;
window.sanitizeTradeUrl = sanitizeTradeUrl;
window.TRADE_ARROW_ICON_SVG = TRADE_ARROW_ICON_SVG;
window.TRADE_OPEN_PRODUCT_ICON_SVG = TRADE_OPEN_PRODUCT_ICON_SVG;
